import * as TabsPrimitive from '@radix-ui/react-tabs'
import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

const Tabs = TabsPrimitive.Root

const tabsListVariants = cva(
	'inline-flex items-center text-(--color-muted-foreground)',
	{
		variants: {
			variant: {
				underline: 'border-b border-(--color-neutral-200)',
				pills: 'gap-0 rounded-lg bg-(--color-secondary) p-1',
				line: 'border-b border-(--color-border)',
			},
		},
		defaultVariants: { variant: 'underline' },
	}
)

const tabsTriggerVariants = cva(
	'inline-flex items-center justify-center whitespace-nowrap text-sm leading-5 transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				underline: [
					'relative px-4 pb-2 font-normal',
					'data-[state=active]:text-(--color-foreground) data-[state=active]:font-medium',
					'after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-full after:bg-(--color-foreground) after:opacity-0',
					'data-[state=active]:after:opacity-100',
				].join(' '),
				pills: [
					'min-w-14 rounded-md px-2 py-1 font-[500]',
					'text-(--color-muted-foreground)',
					'data-[state=active]:bg-(--color-background) data-[state=active]:text-(--color-foreground) data-[state=active]:shadow-sm',
				].join(' '),
				line: [
					'relative h-[60px] min-w-14 gap-2 px-4 font-[600] text-(--color-muted-foreground)',
					'data-[state=active]:text-(--color-foreground)',
					'after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-full after:bg-(--color-primary) after:opacity-0',
					'data-[state=active]:after:opacity-100',
				].join(' '),
			},
		},
		defaultVariants: { variant: 'underline' },
	}
)

export interface TabsListProps
	extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
		VariantProps<typeof tabsListVariants> {}

const TabsList = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.List>,
	TabsListProps
>(({ className, variant, ...props }, ref) => (
	<TabsPrimitive.List
		ref={ref}
		className={cn(tabsListVariants({ variant }), className)}
		{...props}
	/>
))

TabsList.displayName = TabsPrimitive.List.displayName

export interface TabsTriggerProps
	extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
		VariantProps<typeof tabsTriggerVariants> {}

const TabsTrigger = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Trigger>,
	TabsTriggerProps
>(({ className, variant, ...props }, ref) => (
	<TabsPrimitive.Trigger
		ref={ref}
		className={cn(tabsTriggerVariants({ variant }), className)}
		{...props}
	/>
))

TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		ref={ref}
		className={cn('mt-4 focus-visible:outline-none', className)}
		{...props}
	/>
))

TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants, tabsTriggerVariants }
